import React from 'react';
import { Link } from 'react-router';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import ScreenshotFrame from './ScreenshotFrame';
import { STEPS } from './publicContent';
import { C, container, sectionH2, sectionSub, primaryBtnStyle } from './publicStyles';
import usePageMeta from './usePageMeta';
import { PILOT_PATH } from './publicContent';

import step1Shot from '../../assets/public/step-1-describe.png';
import step2Shot from '../../assets/public/step-2-ranked.png';
import step3Shot from '../../assets/public/step-3-analysis.png';
import reportShot from '../../assets/public/report-excerpt.png';

const STEP_SHOTS = [step1Shot, step2Shot, step3Shot];
const REPORT_SHOT = reportShot;

const STEP_CAPTIONS = [
  'Natural-language intent, with manual and investigate modes',
  'Interactive 3D viewer for a generated candidate',
  'Structural analysis panels with DFT options',
];

const FAQ = [
  {
    q: 'What models power Spectra?',
    a: 'MatterGen for inorganic crystal generation, IBM FM4M and GT4SD for molecular work, with pymatgen-backed structural analysis and DFT validation via GPAW and Quantum ESPRESSO. A deterministic router selects the right variant for each objective. You can let the router choose automatically, or manually select and tune model variants for fine-grained control.',
  },
  {
    q: 'What domains are covered?',
    a: 'Inorganic crystals and small molecules today, spanning energy storage, catalysis, electronics, and structural materials objectives. Additional domains are on the roadmap.',
  },
  {
    q: 'How is my data handled?',
    a: 'Researcher-tier runs are stored and may contribute anonymized candidate structures to a pooled research dataset used to improve the platform. Your account data and queries are never sold. Enterprise plans can opt out of pooling entirely, with data residency and run-in-your-cloud options.',
  },
];

export default function HowItWorksPage() {
  usePageMeta({
    title: 'How Spectra Works: Generate, Rank, DFT-Validate | CrimsonScan',
    description: 'From a plain-language target to ranked crystal and molecule candidates, DFT validation with GPAW and Quantum ESPRESSO, and patent-grade reports. See a real run.',
    path: '/how-it-works',
  });
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{'@media (min-width: 700px) { .hiw-text-alt { order: 1; } }'}</style>
      <PublicNav />

      <main>
        <header style={{ ...container, textAlign: 'center', padding: '64px 24px 40px' }}>
          <h1 style={{ color: C.text, fontWeight: 800, fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', marginBottom: 14 }}>
            From idea to validated candidate
          </h1>
          <p style={{ ...sectionSub, margin: '0 auto' }}>
            Three steps, one platform. Here is what a real discovery run looks like.
          </p>
        </header>

        {/* Step walkthrough */}
        {STEPS.map((s, i) => (
          <section key={s.n} style={{ ...container, paddingBottom: 56 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 32, alignItems: 'center',
            }}>
              <div className={'hiw-text' + (i % 2 === 1 ? ' hiw-text-alt' : '')}>
                <div style={{ color: C.crimson, fontWeight: 800, fontSize: 13, letterSpacing: 2, marginBottom: 10 }}>
                  STEP {s.n}
                </div>
                <h2 style={{ color: C.text, fontWeight: 700, fontSize: '1.4rem', marginBottom: 10 }}>{s.title}</h2>
                <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.75, margin: 0 }}>{s.long}</p>
              </div>
              <ScreenshotFrame src={STEP_SHOTS[i]} alt={STEP_CAPTIONS[i]} caption={STEP_CAPTIONS[i]} />
            </div>
          </section>
        ))}

        {/* Sample report */}
        <section style={{ background: C.panel, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ ...container, padding: '64px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32, alignItems: 'center' }}>
              <div>
                <h2 style={sectionH2}>Every run ends in a report you can act on</h2>
                <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.75, marginBottom: 24 }}>
                  Structure, symmetry, predicted properties, comparisons against known materials,
                  synthesis considerations, and economics: assembled into a formal document
                  ready for your team, your patent counsel, or your next grant report.
                </p>
                <a href="/sample-report.pdf" target="_blank" rel="noreferrer" style={primaryBtnStyle}>
                  Download sample report
                </a>
              </div>
              <ScreenshotFrame src={REPORT_SHOT} alt="Scientific report for a demo candidate with abstract, compound identification, and crystal structure sections" caption="The sample scientific report, one of three report types" />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ ...container, padding: '64px 24px' }}>
          <h2 style={{ ...sectionH2, textAlign: 'center', marginBottom: 28 }}>Common questions</h2>
          <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {FAQ.map(f => (
              <div key={f.q} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '20px 24px' }}>
                <h3 style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{f.q}</h3>
                <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section style={{ ...container, textAlign: 'center', paddingBottom: 72 }}>
          <Link to={PILOT_PATH} style={primaryBtnStyle}>Request a pilot</Link>
          <p style={{ color: C.faint, fontSize: 13, marginTop: 14 }}>
            Or <Link to="/pricing" style={{ color: C.info, textDecoration: 'none' }}>see pricing</Link>
          </p>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
